import { useCallback } from 'react';

import { useAuth } from '@/_shared/hooks/useAuth';

/**
 * One definition of "do I own this resource?", for surfaces that mix your own records with other
 * members' (the chat list, a shared chat).
 *
 * Ownership is a plain id comparison — the workspace never enters into it, because every
 * workspace-scoped route already resolves inside the caller's workspace and the server refuses to
 * return a record from any other one. `auth.me` is fetched by the app shell before any workspace
 * route mounts, so this costs no request and `user` is always resolved by the time a page renders.
 */
export function useIsMine(): (ownerUserId: string | null | undefined) => boolean {
  const { user } = useAuth();
  const currentUserId = user?.id ?? null;

  // Memoized so a list page can pass it straight to a row without a fresh closure per render.
  return useCallback(
    (ownerUserId: string | null | undefined) =>
      currentUserId !== null && ownerUserId != null && ownerUserId === currentUserId,
    [currentUserId],
  );
}
